"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { LayoutGrid as Layout, FileText, MousePointerClick, TextCursorInput as FormInput, Bot, MessageSquare, Calendar, Database, ArrowRight, CircleCheck as CheckCircle2, TrendingUp, TriangleAlert as AlertTriangle, Zap, Sparkles, ShieldCheck, Check, Clock, UserCheck } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const funnelStages = [{
  id: 1,
  number: "01",
  title: "Website Optimization",
  description: "Fast, clear, trust-building pages built to convert visitors into phone calls.",
  iconName: "Layout",
  conversionLift: 45,
  missedOpportunities: 8,
  badgeText: "Phase 1: Foundation",
  detailHeadline: "Speed & Clarity First",
  detailBody: "We strip away clutter and optimize your core website structure so every visitor immediately understands your service and takes action.",
  highlights: ["Sub-second load times on mobile & desktop", "High-contrast headline hierarchy", "Trust badges placed directly at decision points"]
}, {
  id: 2,
  number: "02",
  title: "High-Intent Landing Pages",
  description: "Dedicated pages per service and offer matching exact customer search queries.",
  iconName: "FileText",
  conversionLift: 62,
  missedOpportunities: 6,
  badgeText: "Phase 2: Intent Matching",
  detailHeadline: "Tailored Service Experience",
  detailBody: "Stop sending paid or search traffic to a generic homepage. Dedicated pages match local intent and answer questions instantly.",
  highlights: ["Dynamic headlines matching visitor search terms", "Local service radius confirmation", "Zero distracting main menu links"]
}, {
  id: 3,
  number: "03",
  title: "Precision Calls-to-Action",
  description: "The right next step on every screen, making lead generation effortless.",
  iconName: "MousePointerClick",
  conversionLift: 74,
  missedOpportunities: 4,
  badgeText: "Phase 3: Action Triggers",
  detailHeadline: "Guided Visitor Next Steps",
  detailBody: "Strategic action prompts eliminate decision paralysis and guide prospect attention directly to scheduling or requesting a quote.",
  highlights: ["Sticky mobile contact bar for instant calls", "Action-oriented button copy (no generic 'Submit')", "Context-aware popups on scroll depth"]
}, {
  id: 4,
  number: "04",
  title: "Lead Capture Forms",
  description: "Short smart forms that qualify prospects while collecting critical job details.",
  iconName: "FormInput",
  conversionLift: 81,
  missedOpportunities: 3,
  badgeText: "Phase 4: Qualification",
  detailHeadline: "Frictionless Data Collection",
  detailBody: "Eliminate phone tag with multi-step smart forms that gather service details, location, and urgency in under 30 seconds.",
  highlights: ["Address autofill and zip code validation", "Custom pre-qualification logic", "Instant confirmation screen with next steps"]
}, {
  id: 5,
  number: "05",
  title: "AI Chat Assistant",
  description: "Instant answers 24/7 so no website visitor leaves for a competitor.",
  iconName: "Bot",
  conversionLift: 88,
  missedOpportunities: 1,
  badgeText: "Phase 5: 24/7 Response",
  detailHeadline: "Always-On Customer Reception",
  detailBody: "Trained on your business details, our AI chat answers FAQs, captures prospect info, and books appointments even when your office is closed.",
  highlights: ["Sub-2 second response time day or night", "Custom trained on your exact service pricing & FAQs", "Seamless transfer to SMS or human callback"]
}, {
  id: 6,
  number: "06",
  title: "Automated Follow-Ups",
  description: "Instant text and email sequences that revive cold leads automatically.",
  iconName: "MessageSquare",
  conversionLift: 93,
  missedOpportunities: 1,
  badgeText: "Phase 6: Speed to Lead",
  detailHeadline: "Under 60-Second Lead Response",
  detailBody: "Reaching a lead within 2 minutes increases conversion exponentially. Automated SMS sequences start conversations while intent is highest.",
  highlights: ["Instant 2-way SMS confirmation", "Automated multi-day drip for unbooked leads", "Missed-call text back system"]
}, {
  id: 7,
  number: "07",
  title: "Appointment Booking",
  description: "Self-serve scheduling directly into your master team calendar.",
  iconName: "Calendar",
  conversionLift: 97,
  missedOpportunities: 0,
  badgeText: "Phase 7: Self-Scheduling",
  detailHeadline: "Direct Calendar Syncing",
  detailBody: "Let high-intent prospects pick an available service time slot on the spot without back-and-forth emails or phone calls.",
  highlights: ["Real-time team calendar slot sync", "Automated SMS/Email meeting reminders", "Easy 1-click customer rescheduling links"]
}, {
  id: 8,
  number: "08",
  title: "CRM Integration",
  description: "Every lead tracked, tagged, and assigned so zero business opportunity is lost.",
  iconName: "Database",
  conversionLift: 99,
  missedOpportunities: 0,
  badgeText: "Phase 8: Complete Visibility",
  detailHeadline: "Centralized Lead Pipeline",
  detailBody: "Track revenue source from first AI click to closed contract. Automatic lead assignment ensures complete pipeline accountability.",
  highlights: ["Automated lead stage updates", "Full conversation & call activity timeline", "Marketing ROI & attribution dashboard"]
}];
export default function Interactiveleadgenfunnel() {
  const [activeStageId, setActiveStageId] = useState<number>(1);
  const activeStage = funnelStages.find(stage => stage.id === activeStageId) || funnelStages[0];
  const getStageIcon = (iconName: string) => {
    switch (iconName) {
      case "Layout":
        return <Layout className="size-5 shrink-0" />;
      case "FileText":
        return <FileText className="size-5 shrink-0" />;
      case "MousePointerClick":
        return <MousePointerClick className="size-5 shrink-0" />;
      case "FormInput":
        return <FormInput className="size-5 shrink-0" />;
      case "Bot":
        return <Bot className="size-5 shrink-0" />;
      case "MessageSquare":
        return <MessageSquare className="size-5 shrink-0" />;
      case "Calendar":
        return <SafeHydrate><Calendar className="size-5 shrink-0" /></SafeHydrate>;
      case "Database":
        return <Database className="size-5 shrink-0" />;
      default:
        return <Zap className="size-5 shrink-0" />;
    }
  };
  const renderDashboardMockup = (stageId: number) => {
    switch (stageId) {
      case 1:
        return <div className="space-y-3 rounded-lg border border-border bg-background/50 p-4">
            <div className="flex items-center justify-between border-b border-border/60 pb-3">
              <div className="flex items-center gap-2">
                <div className="size-3 rounded-full bg-[var(--accent-deep-red)]" />
                <div className="size-3 rounded-full bg-muted-foreground/30" />
                <div className="size-3 rounded-full bg-muted-foreground/30" />
                <span className="ml-2 text-xs font-mono text-muted-foreground">clearlinegeo.com</span>
              </div>
              <Badge variant="outline" className="border-[var(--accent-deep-red)]/40 text-[var(--accent-deep-red)] text-2xs">
                Score: 99/100
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 py-1">
              <div className="rounded bg-card p-2 text-center border border-border/40">
                <p className="text-3xs text-muted-foreground">LCP Speed</p>
                <p className="text-xs font-bold text-foreground">0.4s</p>
              </div>
              <div className="rounded bg-card p-2 text-center border border-border/40">
                <p className="text-3xs text-muted-foreground">Mobile Score</p>
                <p className="text-xs font-bold text-foreground">100%</p>
              </div>
              <div className="rounded bg-card p-2 text-center border border-border/40">
                <p className="text-3xs text-muted-foreground">SEO Index</p>
                <p className="text-xs font-bold text-foreground">A+</p>
              </div>
            </div>
            <div className="rounded border border-dashed border-border p-3 text-center">
              <p className="text-xs font-semibold text-foreground">High-Converting Hero Banner Active</p>
              <p className="text-2xs text-muted-foreground mt-0.5">Trust signals &amp; instant quote form rendering</p>
            </div>
          </div>;
      case 2:
        return <div className="space-y-3 rounded-lg border border-border bg-background/50 p-4">
            <div className="flex items-center justify-between">
              <Badge className="bg-[var(--accent-deep-red)]/15 text-[var(--accent-deep-red)] border-[var(--accent-deep-red)]/30 text-xs">
                Target: Local Service Leads
              </Badge>
              <span className="text-xs text-muted-foreground">Intent Match: 98%</span>
            </div>
            <div className="rounded bg-card p-3 border border-border/60 space-y-2">
              <div className="h-3 w-3/4 rounded bg-foreground/20" />
              <div className="h-2 w-1/2 rounded bg-muted-foreground/30" />
              <div className="pt-2 flex gap-2">
                <div className="h-7 flex-1 rounded bg-[var(--accent-deep-red)] text-white text-3xs font-medium flex items-center justify-center">
                  Book Immediate Service
                </div>
                <div className="h-7 w-20 rounded border border-border bg-muted/40 text-3xs flex items-center justify-center text-muted-foreground">
                  View Rates
                </div>
              </div>
            </div>
          </div>;
      case 3:
        return <div className="space-y-3 rounded-lg border border-border bg-background/50 p-4">
            <div className="p-3 bg-card rounded border border-border flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-foreground">Primary Action Trigger</p>
                <p className="text-2xs text-muted-foreground">Positioned above the fold</p>
              </div>
              <Button size="sm" className="bg-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 text-white text-xs gap-1.5 shadow-sm">
                <span>Book a Call</span>
                <ArrowRight className="size-3" />
              </Button>
            </div>
            <div className="flex items-center gap-2 text-2xs text-muted-foreground px-1">
              <Check className="size-3 text-[var(--accent-deep-red)]" />
              <span>3 slots available today in your zip code</span>
            </div>
          </div>;
      case 4:
        return <div className="space-y-2 rounded-lg border border-border bg-background/50 p-4">
            <p className="text-xs font-semibold text-foreground mb-1">Interactive Qualifier Preview</p>
            <div className="space-y-2">
              <div className="rounded border border-border bg-card p-2 text-xs text-foreground flex justify-between items-center">
                <span>Full Name: Alex Vance</span>
                <CheckCircle2 className="size-3.5 text-[var(--accent-deep-red)] shrink-0" />
              </div>
              <div className="rounded border border-border bg-card p-2 text-xs text-foreground flex justify-between items-center">
                <span>Phone: (555) 234-8901</span>
                <CheckCircle2 className="size-3.5 text-[var(--accent-deep-red)] shrink-0" />
              </div>
              <div className="rounded border border-[var(--accent-deep-red)]/50 bg-[var(--accent-deep-red)]/10 p-2 text-xs text-foreground flex justify-between items-center">
                <span>Service Needed: AI Growth Strategy</span>
                <Badge variant="outline" className="text-3xs border-[var(--accent-deep-red)]/50 text-[var(--accent-deep-red)] bg-card">
                  Verified
                </Badge>
              </div>
            </div>
          </div>;
      case 5:
        return <div className="space-y-2 rounded-lg border border-border bg-background/50 p-4">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <div className="flex items-center gap-2">
                <div className="size-2 rounded-full bg-[var(--accent-deep-red)] animate-pulse" />
                <span className="text-xs font-medium text-foreground">ClearLine AI Assistant</span>
              </div>
              <span className="text-3xs text-muted-foreground">24/7 Active</span>
            </div>
            <div className="space-y-2 pt-1 text-2xs">
              <div className="rounded-lg bg-muted/60 p-2 text-muted-foreground max-w-[85%]">
                Hi, do you have availability for a free AI checkup tomorrow?
              </div>
              <div className="rounded-lg bg-[var(--accent-deep-red)]/20 border border-[var(--accent-deep-red)]/30 p-2 text-foreground max-w-[85%] ml-auto text-right">
                Yes! We have openings at 10:00 AM and 2:30 PM. Should I lock in 10:00 AM for you?
              </div>
            </div>
          </div>;
      case 6:
        return <div className="space-y-2.5 rounded-lg border border-border bg-background/50 p-4">
            <p className="text-xs font-semibold text-foreground">Speed-to-Lead Automation Triggered</p>
            <div className="space-y-2 text-2xs">
              <div className="flex items-center justify-between p-2 rounded bg-card border border-border">
                <div className="flex items-center gap-2">
                  <MessageSquare className="size-3.5 text-[var(--accent-deep-red)]" />
                  <span className="text-foreground">SMS Confirmation Sent</span>
                </div>
                <span className="text-muted-foreground">0 min</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-card border border-border">
                <div className="flex items-center gap-2">
                  <SafeHydrate><Calendar className="size-3.5 text-[var(--accent-deep-red)]" /></SafeHydrate>
                  <span className="text-foreground">Calendar Invite Dispatched</span>
                </div>
                <span className="text-muted-foreground">1 min</span>
              </div>
            </div>
          </div>;
      case 7:
        return <div className="space-y-3 rounded-lg border border-border bg-background/50 p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground">Select Available Time</span>
              <span className="text-3xs text-muted-foreground">Timezone: Local</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 text-center rounded border border-border bg-card text-2xs text-muted-foreground line-through">
                09:00 AM
              </div>
              <div className="p-2 text-center rounded border border-[var(--accent-deep-red)] bg-[var(--accent-deep-red)]/20 text-2xs font-semibold text-foreground">
                10:30 AM
              </div>
              <div className="p-2 text-center rounded border border-border bg-card text-2xs text-foreground">
                02:00 PM
              </div>
            </div>
            <div className="rounded bg-card p-2 text-2xs text-center border border-border/60 text-muted-foreground">
              Direct calendar invite synced with your team account
            </div>
          </div>;
      case 8:
        return <div className="space-y-2 rounded-lg border border-border bg-background/50 p-4">
            <p className="text-xs font-semibold text-foreground mb-2">Live Lead Pipeline Board</p>
            <div className="grid grid-cols-3 gap-2 text-3xs">
              <div className="rounded border border-border bg-card p-2">
                <p className="font-bold text-foreground">New Lead</p>
                <p className="text-muted-foreground mt-1 font-mono">+1 (555) 234</p>
              </div>
              <div className="rounded border border-[var(--accent-deep-red)]/40 bg-[var(--accent-deep-red)]/10 p-2">
                <p className="font-bold text-[var(--accent-deep-red)]">Qualified</p>
                <p className="text-foreground mt-1 font-mono">Alex V.</p>
              </div>
              <div className="rounded border border-border bg-card p-2">
                <p className="font-bold text-foreground">Booked</p>
                <p className="text-muted-foreground mt-1 font-mono">$2,400 Deal</p>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <section id="interactiveleadgenfunnel" data-nav="dark" className="relative bg-background text-foreground py-32 md:py-44 overflow-hidden">
      {}
      <div className="pointer-events-none absolute inset-0 bg-radial from-[var(--accent-deep-red)]/10 via-transparent to-transparent opacity-60" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div className="max-w-3xl mb-16 md:mb-20">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
            <Sparkles className="size-4 text-[var(--accent-deep-red)]" />
            <span>HOW IT WORKS</span>
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
            An Interactive System{" "}
            <span className="italic text-[var(--accent-deep-red)] underline decoration-[var(--accent-deep-red)]/30 underline-offset-8">
              Optimized
            </span>{" "}
            to Convert AI Discovery into Loyal Clients
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every step of your prospect journey is mapped and automated. From first AI query or search finding to a confirmed booking in your calendar, here is how we turn lost traffic into measurable revenue.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {}
          <div className="lg:col-span-7 space-y-3.5">
            {funnelStages.map((stage, i) => {
            const isActive = stage.id === activeStageId;
            return <div key={stage.id} data-index={i} className="group">
                  <Button variant="ghost" onClick={() => setActiveStageId(stage.id)} className={`w-full text-left justify-start h-auto p-4 sm:p-5 rounded-xl border transition-all duration-200 relative overflow-hidden ${isActive ? "bg-card border-[var(--accent-deep-red)]/80 shadow-md text-card-foreground" : "bg-card/60 border-border hover:border-[var(--accent-deep-red)]/50 hover:bg-card/90 text-card-foreground/80"}`}>
                    {}
                    {isActive && <motion.div layoutId="activeIndicator" className="absolute left-0 top-0 bottom-0 w-1.5 bg-[var(--accent-deep-red)]" transition={{
                  duration: 0.2
                }} />}

                    <div className="flex items-start gap-4 w-full">
                      {}
                      <div className={`p-2.5 rounded-lg border shrink-0 transition-colors duration-200 ${isActive ? "bg-[var(--accent-deep-red)]/15 border-[var(--accent-deep-red)]/40 text-[var(--accent-deep-red)]" : "bg-muted/50 border-border text-muted-foreground group-hover:text-foreground"}`}>
                        {getStageIcon(stage.iconName)}
                      </div>

                      {}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-2xs font-mono font-semibold uppercase tracking-wider text-muted-foreground">
                            Step {stage.number}
                          </span>
                          {isActive && <Badge variant="secondary" className="bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] border border-[var(--accent-deep-red)]/20 text-3xs font-medium">
                              Active View
                            </Badge>}
                        </div>
                        <h3 className="text-base sm:text-lg font-semibold text-foreground tracking-tight">
                          {stage.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </div>
                  </Button>

                  {}
                  <div className="block lg:hidden mt-2">
                    {isActive && <Card className="border-[var(--accent-deep-red)]/30 bg-card/90 mt-3">
                        <CardContent className="p-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <Badge className="bg-[var(--accent-deep-red)] text-white text-3xs">
                              {stage.badgeText}
                            </Badge>
                            <span className="text-xs text-muted-foreground font-mono">
                              Lift: +{stage.conversionLift}%
                            </span>
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-foreground">
                              {stage.detailHeadline}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                              {stage.detailBody}
                            </p>
                          </div>
                          {renderDashboardMockup(stage.id)}
                        </CardContent>
                      </Card>}
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="hidden lg:block lg:col-span-5 lg:sticky lg:top-24">
            <Card className="bg-card border-border/80 shadow-2xl overflow-hidden relative">
              <CardContent className="p-6 space-y-6">
                {}
                <AnimatePresence mode="wait">
                  <motion.div key={activeStage.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} className="space-y-6">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-[var(--accent-deep-red)] text-white font-medium text-xs px-2.5 py-1">
                        {activeStage.badgeText}
                      </Badge>
                      <span className="text-xs font-mono text-muted-foreground">
                        Step {activeStage.number} of 08
                      </span>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-foreground tracking-tight">
                        {activeStage.detailHeadline}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed">
                        {activeStage.detailBody}
                      </p>
                    </div>

                    {}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <Zap className="size-3.5 text-[var(--accent-deep-red)]" />
                        <span>Live Stage Preview</span>
                      </p>
                      {renderDashboardMockup(activeStage.id)}
                    </div>

                    <Separator className="bg-border/60" />

                    {}
                    <div className="space-y-4">
                      {}
                      <div>
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="font-semibold text-foreground flex items-center gap-1.5">
                            <TrendingUp className="size-3.5 text-[var(--accent-deep-red)]" />
                            Estimated Conversion Lift
                          </span>
                          <span className="font-bold text-[var(--accent-deep-red)] font-mono">
                            +{activeStage.conversionLift}%
                          </span>
                        </div>
                        <Progress value={activeStage.conversionLift} className="h-2 bg-muted/60" />
                      </div>

                      {}
                      <div className="rounded-lg bg-muted/40 border border-border p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="size-4 text-[var(--accent-deep-red)] shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-foreground">
                              Uncaptured Opportunities
                            </p>
                            <p className="text-3xs text-muted-foreground">
                              Leads dropped without this stage
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-base font-bold font-mono text-[var(--accent-deep-red)]">
                            {activeStage.missedOpportunities} / mo
                          </span>
                        </div>
                      </div>
                    </div>

                    {}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-foreground">Key Capabilities:</p>
                      <ul className="space-y-1.5">
                        {activeStage.highlights.map((highlight, idx) => <li key={idx} data-index={idx} className="text-2xs text-muted-foreground flex items-center gap-2">
                            <Check className="size-3 text-[var(--accent-deep-red)] shrink-0" />
                            <span>{highlight}</span>
                          </li>)}
                      </ul>
                    </div>

                    {}
                    <div className="pt-2">
                      <Button asChild className="w-full bg-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 text-white font-medium py-2.5 shadow-md">
                        <Link to="/ContactPage">
                          <span>Get Your Free AI Visibility Checkup</span>
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </CardContent>
            </Card>
          </div>
        </div>

        {}
        <div className="mt-20 rounded-2xl border border-[var(--accent-deep-red)]/30 bg-card p-8 md:p-12 text-center relative overflow-hidden shadow-xl">
          <div className="pointer-events-none absolute -right-12 -top-12 size-64 rounded-full bg-[var(--accent-deep-red)]/10 blur-3xl" />
          <div className="max-w-2xl mx-auto space-y-4 relative z-10">
            <Badge variant="outline" className="border-[var(--accent-deep-red)]/40 text-[var(--accent-deep-red)] text-xs">
              Ready to Upgrade Your Lead Engine?
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              Start Capturing High-Intent Leads Today
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We audit your current online footprint and set up this exact 8-stage conversion funnel for your business in days.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button asChild size="lg" className="w-full sm:w-auto bg-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 text-white gap-2">
                <Link className="underline cursor-pointer" to="/PricingPlansPage">
                  <span>Get started now</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border hover:bg-muted/50 text-foreground">
                <Link to="/VisibilityCheckupPage">
                  <span>Get Free AI Checkup</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}